import React from "react";
import {Cross2Icon} from "@radix-ui/react-icons";
import {Download, Mail} from "lucide-react";
import {Table} from "@tanstack/react-table";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {DataTableViewOptions} from "./data-table-view-options";
import {DataTableFacetedFilter} from "@/components/data-table/data-table-faceted-filter.tsx";
import {ToolbarOptions} from "@/types/data-table/ToolbarOptions.ts";
import {
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {handleExportPDF, handleExportXLSX} from "@/utils/export-helper.ts";
import {useTranslation} from "react-i18next";

interface DataTableToolbarProps<TData> {
    table: Table<TData>;
    toolbarOptions: ToolbarOptions;
    id: string;
}

export function DataTableToolbar<TData>({
                                            table, toolbarOptions, id
                                        }: DataTableToolbarProps<TData>) {
    const {t} = useTranslation();
    const isFiltered = table.getState().columnFilters.length > 0;
    const hasSelection = table.getIsSomeRowsSelected();

    // Determine the data to export: selected rows if any, else all (sorted/filtered/core)
    let exportData: TData[];
    let filename: string;
    if (hasSelection) {
        exportData = table.getSelectedRowModel().rows.map((row) => row.original);
        filename = `${id}_selected`;
    } else if (table.getState().sorting.length > 0) {
        exportData = table.getSortedRowModel().rows.map((row) => row.original);
        filename = isFiltered ? `${id}_filtered_and_sorted` : `${id}_sorted`;
    } else if (isFiltered) {
        exportData = table.getFilteredRowModel().rows.map((row) => row.original);
        filename = `${id}_filtered`;
    } else {
        exportData = table.getCoreRowModel().rows.map((row) => row.original);
        filename = id;
    }

    const sendEmailColumn = toolbarOptions.sendEmailColumn;
    const handleSendEmail = () => {
        if (!sendEmailColumn) return;
        const rows = hasSelection
            ? table.getSelectedRowModel().rows
            : table.getState().sorting.length > 0
                ? table.getSortedRowModel().rows
                : isFiltered
                    ? table.getFilteredRowModel().rows
                    : table.getCoreRowModel().rows;
        const emails = rows
            .map((row) => {
                const value = row.getValue(sendEmailColumn);
                return typeof value === "string" ? value.trim() : "";
            })
            .filter((e) => e.length > 0);
        const unique = [...new Set(emails)];
        if (unique.length === 0) return;
        const mailto = `mailto:${unique.join(",")}`;
        window.location.href = mailto;
    };

    return (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 rounded-xl border border-border bg-bg-elevated/60 p-3">
                <Input
                    placeholder={`Search ${toolbarOptions.searchableColumn}...`}
                    value={(table.getColumn(toolbarOptions.searchableColumn)?.getFilterValue() as string) ?? ""}
                    onChange={(event) =>
                        table.getColumn(toolbarOptions.searchableColumn)?.setFilterValue(event.target.value)
                    }
                    className="w-full sm:w-[220px]"
                />
                <div className="flex flex-wrap gap-2">
                    {toolbarOptions.filters.map((filter) => (
                        <React.Fragment key={filter.column}>
                            {table.getColumn(filter.column) && (
                                <DataTableFacetedFilter
                                    column={table.getColumn(filter.column)}
                                    title={filter.title}
                                    tableId={id}
                                />
                            )}
                        </React.Fragment>
                    ))}
                    {isFiltered && (
                        <Button
                            variant="ghost"
                            onClick={() => table.resetColumnFilters()}
                            className="h-10 px-3"
                        >
                            {t("dataTable.toolbar.reset")}
                            <Cross2Icon className="ml-2 h-4 w-4"/>
                        </Button>
                    )}
                </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
                <div className="flex gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline">
                                <Download className="sm:mr-2 h-4 w-4"/>
                                <span className="hidden sm:inline"> {t("dataTable.toolbar.export")}</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56">
                            <DropdownMenuLabel>{t("dataTable.toolbar.exportAs")}</DropdownMenuLabel>
                            <DropdownMenuSeparator/>
                            <DropdownMenuItem
                                onClick={() => handleExportXLSX(exportData, table.getVisibleFlatColumns(), filename)}
                            >
                                Excel (XLS)
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => handleExportPDF(exportData, table.getVisibleFlatColumns(), filename)}
                            >
                                PDF
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <DataTableViewOptions table={table}/>
                    {/* Mobile: icon-only Send Email beside Export + View */}
                    {sendEmailColumn && (
                        <Button variant="outline" className="sm:hidden" onClick={handleSendEmail}>
                            <Mail className="h-4 w-4"/>
                        </Button>
                    )}
                </div>
                {/* Desktop: full-width Send Email below Export + View */}
                {sendEmailColumn && (
                    <Button variant="outline" className="hidden w-full sm:flex" onClick={handleSendEmail}>
                        <Mail className="mr-2 h-4 w-4 shrink-0"/>
                        {t("dataTable.toolbar.sendEmail")}
                    </Button>
                )}
            </div>
        </div>
    );
}
